import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import {
  SHORTLIST_CLEAR,
  SHORTLIST_DELETE,
  SHORTLIST_UPLOADS_CANCEL,
} from '@disco/constants/ActionTypes'
import { taskRunner } from '@disco/utils/upload'
import cuid from 'cuid'
import get from 'lodash/get'
import type { UINotification } from 'types/notification'
import type { GetState } from 'types/redux'
import type { Shortlist, ShortlistSection, TempItem } from 'types/shortlist'
import type { Track } from 'types/track'
import {
  getDefaultShortlistGroups,
  insertTempItems,
  removeTempItem,
} from '@disco/state-management/actions/ShortlistActions'
import type { UploadProvider } from 'types/upload'
import { verifyFiles } from '@disco/utils/upload'
import { getShortlistUploadTaskIds } from '@disco/state-management/selectors/shortlists'
/**
 * Deletes a shortlist and cancels all associated upload tasks.
 * @param shortlistId - ID of the shortlist to delete
 * @returns Thunk that deletes shortlist and removes all upload tasks
 */
export const deleteShortlist =
  (
    shortlistId: string,
  ): ((dispatch: any, getState: GetState) => Promise<any>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const tasks = getShortlistUploadTaskIds(getState(), {
      id: shortlistId,
    })
    tasks.forEach(t => taskRunner.removeTask(t))
    return await dispatch({
      type: SHORTLIST_DELETE,
      shortlistId,
    })
  }
/**
 * Clears a shortlist and resets it to default state with user preferences.
 * @param shortlistId - ID of the shortlist to clear
 * @returns Thunk that clears shortlist and applies default groups and settings
 */
export function clearShortlist(
  shortlistId: string,
): (dispatch: any, getState: GetState) => any {
  return (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    const tasks = getShortlistUploadTaskIds(getState(), {
      id: shortlistId,
    })
    tasks.forEach(t => taskRunner.removeTask(t))
    const { user } = getState()
    return dispatch({
      type: SHORTLIST_CLEAR,
      shortlistId,
      defaultGroups: getDefaultShortlistGroups(user.groups),
      default_zip_mode: get(
        user,
        'business.public_metadata.default_zip_mode',
        'optimized',
      ),
      share_mode: get(user, 'business.public_metadata.share_mode', 'standard'),
    })
  }
}
/**
 * Cancels an individual upload task for a shortlist item.
 * @param shortlistId - ID of the shortlist containing the item
 * @param tempItem - Temporary item or track whose upload task to cancel
 * @returns Thunk that cancels upload task and removes temp item
 */
export const cancelShortlistUploadTask =
  (shortlistId: string, tempItem: TempItem | Track): ((dispatch: any) => any) =>
  (dispatch: (...args: Array<any>) => any) => {
    if (tempItem.tempId) {
      taskRunner.removeTask(tempItem.tempId)
    }

    return dispatch(removeTempItem(shortlistId, tempItem))
  }
// Cancel all upload promises, next remove all upload related UI elements
/**
 * Cancels all upload tasks across all shortlists.
 * @returns Thunk that cancels all upload tasks and removes upload UI elements
 */
export const uploadsCancel =
  (): ((dispatch: any) => any) => (dispatch: (...args: Array<any>) => any) => {
    taskRunner.cancelTasks()
    return dispatch({
      type: SHORTLIST_UPLOADS_CANCEL,
    })
  }
/**
 * Uploads files to a shortlist using a specified upload provider.
 * @param provider - Upload provider to handle the file upload
 * @param shortlistId - ID of the shortlist to upload files to
 * @param files - Array of files to upload
 * @param onNotification - Callback function to handle notifications
 * @param options - Optional configuration for upload behavior
 * @param options.positionId - Position ID for inserting items
 * @param options.targetSection - Target section for the upload
 * @param options.context - Context string for analytics
 * @param options.wasDragged - Whether files were dragged and dropped
 * @returns Thunk that uploads files with verification, analytics, and error handling
 */
export const uploadProviderFiles =
  (
    provider: UploadProvider,
    shortlistId: string,
    files: any[],
    onNotification: (notification: UINotification) => void,
    options?: {
      positionId?: number
      targetSection?: ShortlistSection
      context?: string
      wasDragged?: boolean
    },
  ): ((dispatch: any, getState: GetState) => Promise<Shortlist | void>) =>
  async (dispatch: (...args: Array<any>) => any, getState: GetState) => {
    // Prepare temp items and verify files
    const tempItems = verifyFiles(
      provider.prepareTempItems(files),
      onNotification,
    )
    // If there are no valid files, just return as message has been shown
    if (!tempItems.length) return
    // Generate a unique upload ID
    const uploadId = cuid()
    // Create a promise which will be resolved when the upload is complete
    const promise = new Promise<Shortlist>((resolve, reject) => {
      api.pendingUploadPromises[shortlistId] = {
        resolve,
        reject,
      }
    }).finally(() => {
      delete api.pendingUploadPromises[shortlistId]
    })

    try {
      // Send to Mixpanel
      mixpanel.sendPureEvent(
        options?.wasDragged ? 'Drag file(s) to upload' : 'Upload file(s)',
        {
          modal: options?.wasDragged ? undefined : 'Upload',
          context: options?.context || 'Unknown',
          type: provider.title,
          count: tempItems.length,
        },
      )
      // Upload the files using the provider
      await provider.upload(
        shortlistId,
        uploadId,
        tempItems,
        dispatch,
        getState,
      )
      // Insert the temp items into the shortlist
      dispatch(
        insertTempItems(
          shortlistId,
          tempItems,
          options?.positionId,
          options?.targetSection,
        ),
      )
      return promise
    } catch (error) {
      api.pendingUploadPromises?.[shortlistId]?.reject(error)
      onNotification({
        message: `Sorry! A server error prevented this import from starting. Please try again, and if it fails, let us know.`,
        level: 'error',
      })
      throw error
    }
  }
